"use client";

import Image from "next/image";
import Link from "next/link";
import { Phone, ArrowRight, Shield, AlertTriangle, FileText, Heart, ExternalLink, Star } from "lucide-react";
import { BRAND } from "@/lib/brand";

const F = { display: "'Plus Jakarta Sans', sans-serif", body: '"Barlow", sans-serif', mono: '"DM Mono", monospace' };
const C = { gold: "#D4A84B", white: "#ffffff", w82: "rgba(255,255,255,0.82)", w55: "rgba(255,255,255,0.70)", w30: "rgba(255,255,255,0.30)", navy: "#080F1A", navyMid: "#0d1929" };
const CARD: React.CSSProperties = { background: "rgba(5,13,24,0.72)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.15)", borderTop: `2px solid ${C.gold}`, borderRadius: 14, padding: "2rem", display: "flex", flexDirection: "column" };
const PANEL: React.CSSProperties = { background: "rgba(5,13,24,0.76)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 16, padding: "2.5rem" };
const LABEL: React.CSSProperties = { color: C.gold, fontFamily: F.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", display: "block", marginBottom: "0.6rem" };
const H2: React.CSSProperties = { color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 3rem)", marginBottom: "0.75rem", lineHeight: 1.1 };
const H3: React.CSSProperties = { color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.3rem", marginBottom: "0.75rem" };
const P: React.CSSProperties = { color: C.w82, fontSize: "0.95rem", lineHeight: 1.75, fontFamily: F.body };
const OVERLAY = (o: number): React.CSSProperties => ({ position: "absolute", inset: 0, background: `rgba(5,13,24,${o})`, zIndex: 1 });
const CONTENT: React.CSSProperties = { position: "relative", zIndex: 2, padding: "5rem 1.5rem", maxWidth: 1200, margin: "0 auto", width: "100%" };
const SEC: React.CSSProperties = { position: "relative", overflow: "hidden", backgroundColor: C.navy };
const GOLDLINE: React.CSSProperties = { width: 48, height: 2, background: C.gold, borderRadius: 2, margin: "16px 0" };
const BTN_RED: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 10, background: "linear-gradient(135deg, #DC2626 0%, #dc2626 100%)", color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.1rem", borderRadius: 12, padding: "16px 36px", textDecoration: "none", border: "none", letterSpacing: "1px", boxShadow: "0 8px 32px rgba(220,38,38,0.55), 0 2px 8px rgba(220,38,38,0.4)" };
const BTN_GOLD: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", color: C.gold, fontFamily: F.body, fontWeight: 600, fontSize: "1rem", border: `1.5px solid ${C.gold}`, borderRadius: 10, padding: "13px 26px", textDecoration: "none" };

const fears = [
  {
    icon: AlertTriangle,
    title: "Who Is In My Home?",
    fear: "After every storm season, unlicensed contractors flood Arizona neighborhoods — knocking on doors, offering fast fixes, disappearing mid-project. The Arizona Attorney General issued a consumer alert in September 2025 warning homeowners about this exact problem. You don't know who you're letting through your front door.",
    answer: "DRR employees only. Every crew member is background-checked, credentialed, and arrives in uniform with company ID. We are licensed under AZ ROC KB-1 Dual #349012 — you can verify us at roc.az.gov before we ever set foot in your home. No subcontractors. No strangers.",
  },
  {
    icon: FileText,
    title: "Insurance Confusion",
    fear: "You've never filed a claim before. You don't know what's covered, what to say to your adjuster, or how to avoid accidentally hurting your own claim. The process feels designed to overwhelm you — and it is.",
    answer: "We communicate directly with your insurance carrier so you don't have to fight alone. We provide the documentation your adjuster needs — moisture readings, photo evidence, Xactimate scope — and we walk you through every step in plain language. No jargon. No surprises.",
  },
  {
    icon: Heart,
    title: "Being Taken Advantage Of",
    fear: "Inflated scopes you can't verify. Bills that don't match the estimate. Work that starts fast and then stalls. You've heard the stories — or maybe you've lived one. When your home is damaged, you're vulnerable, and not every contractor respects that.",
    answer: "Written Xactimate scope before work begins — you see every line item before we start. One project manager, one direct phone number, daily updates. From day one to the day the project is completed, you will never wonder what's happening in your own home.",
  },
];

const creds = [
  { label: "AZ ROC #349012" },
  { label: "BBB A+ Accredited" },
  { label: `${BRAND.rating}★ — ${BRAND.reviewCount} Google Reviews` },
  { label: "IICRC Certified Firm" },
];

export default function ResidentialPage() {
  return (
    <>
      {/* ═══ HERO ═══ */}
      <section style={{ ...SEC, minHeight: "90vh", display: "flex", alignItems: "center" }}>
        <Image src="/images/arizona-landmarks/saguaro-cactus-desert-arizona.jpg" alt="Arizona desert landscape" fill style={{ objectFit: "cover" }} priority />
        <div style={OVERLAY(0.52)} />
        <div style={{ ...CONTENT, maxWidth: 900, textAlign: "center" }}>
          <span style={LABEL}>Residential Restoration</span>
          <h1 style={{ color: C.white, fontFamily: F.display, fontWeight: 800, fontSize: "clamp(2.2rem, 5vw, 4rem)", lineHeight: 1.05, marginBottom: 20 }}>
            You Don&rsquo;t Know Us Yet.<br />Here&rsquo;s How You Will.
          </h1>
          <div style={{ ...GOLDLINE, margin: "20px auto" }} />
          <p style={{ ...P, fontSize: "1.1rem", maxWidth: 640, margin: "0 auto 16px" }}>
            Licensed, verified, background-checked DRR employees — not strangers from a subcontractor list.
            We walk you through every step so you&rsquo;re never in the dark.
          </p>
          <p style={{ color: C.gold, fontFamily: F.display, fontWeight: 600, fontSize: "1rem", marginBottom: 36 }}>
            We don&rsquo;t dip our toes in the water. We go all in.
          </p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            </div>
        </div>
      </section>

      {/* ═══ PAIN SECTION ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid }}>
        <div style={CONTENT}>
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <span style={LABEL}>We Understand</span>
            <h2 style={H2}>What Homeowners Are Really Worried About</h2>
            <div style={{ ...GOLDLINE, margin: "16px auto" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1.5rem" }}>
            {fears.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} style={CARD}>
                  <Icon size={28} style={{ color: C.gold, marginBottom: 16 }} />
                  <h3 style={H3}>{f.title}</h3>
                  <p style={{ ...P, fontSize: "0.88rem", marginBottom: 20 }}>
                    <span style={{ color: "rgba(220,38,38,0.9)", fontWeight: 600 }}>The worry: </span>
                    {f.fear}
                  </p>
                  <div style={{ background: "rgba(212,168,75,0.08)", border: "1px solid rgba(212,168,75,0.20)", borderRadius: 10, padding: "1rem", marginTop: "auto" }}>
                    <p style={{ ...P, fontSize: "0.88rem" }}>
                      <span style={{ color: C.gold, fontWeight: 600 }}>What DRR does differently: </span>
                      {f.answer}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ CONSUMER PROTECTION CALLOUT ═══ */}
      <section style={{ ...SEC, padding: "60px 24px" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.22)", borderRadius: 14, padding: "2rem 2.5rem" }}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              <AlertTriangle size={28} style={{ color: "#D4A84B", flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 260 }}>
                <h3 style={{ color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.2rem", marginBottom: 10 }}>
                  Arizona Consumer Alert — Unlicensed Contractors
                </h3>
                <p style={{ ...P, fontSize: "0.9rem", marginBottom: 14 }}>
                  After a storm, unlicensed contractors flood Arizona neighborhoods. The Arizona Attorney General
                  issued a consumer alert in September 2025 warning homeowners to verify contractor licensing
                  before signing any agreement. Always check at{" "}
                  <a href="https://roc.az.gov" target="_blank" rel="noopener noreferrer" style={{ color: C.gold, fontWeight: 600, textDecoration: "underline" }}>
                    roc.az.gov
                  </a>{" "}
                  before letting anyone into your home.
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1rem" }}>
                    DRR: AZ ROC KB-1 Dual #349012
                  </span>
                  <a href="https://roc.az.gov" target="_blank" rel="noopener noreferrer" style={{ color: C.gold, fontSize: "0.82rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none" }}>
                    Verify Now <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CREDENTIALS STRIP ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid, padding: "60px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <span style={LABEL}>Why Homeowners Trust DRR</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", justifyContent: "center" }}>
            {creds.map((c) => (
              <div key={c.label} style={{ background: "rgba(5,13,24,0.60)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.14)", borderTop: `2px solid ${C.gold}`, borderRadius: 10, padding: "0.9rem 1.25rem", display: "flex", alignItems: "center", gap: 8 }}>
                <Shield size={16} style={{ color: C.gold, flexShrink: 0 }} />
                <span style={{ color: C.white, fontFamily: F.body, fontWeight: 600, fontSize: "0.82rem" }}>{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ REVIEW QUOTE ═══ */}
      <section style={{ ...SEC, minHeight: "40vh" }}>
        <Image src="/images/arizona-landmarks/sedona-cathedral-rock-arizona.jpg" alt="Arizona red rocks" fill style={{ objectFit: "cover" }} />
        <div style={OVERLAY(0.60)} />
        <div style={{ ...CONTENT, maxWidth: 700, textAlign: "center" }}>
          <div style={{ ...PANEL, padding: "3rem" }}>
            <div style={{ display: "flex", justifyContent: "center", gap: 4, marginBottom: 20 }}>
              {[1,2,3,4,5].map((s) => <Star key={s} size={22} style={{ color: C.gold, fill: C.gold }} />)}
            </div>
            <p style={{ fontSize: "clamp(1.1rem, 2.5vw, 1.5rem)", fontFamily: F.display, fontWeight: 700, fontStyle: "italic", color: C.white, lineHeight: 1.35, marginBottom: 16 }}>
              &ldquo;They explained everything before they started, checked in with us every day,
              and left our home cleaner than they found it. We never felt like we were in the dark
              about what was happening or what it would cost. That meant everything to us.&rdquo;
            </p>
            <p style={{ fontSize: "0.82rem", color: C.w55, fontFamily: F.mono, letterSpacing: "1px", textTransform: "uppercase" }}>
              Verified Homeowner — Phoenix Metro Area
            </p>
            <a href="https://search.google.com/local/writereview?placeid=ChIJAyTK9CUPK4cR7MDJ8MtNbgM" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 16, fontSize: "0.82rem", color: C.gold, fontWeight: 600, textDecoration: "none" }}>
              Read more reviews <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </section>

      {/* ═══ WHAT TO EXPECT ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid, padding: "80px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
            <span style={LABEL}>Your Experience</span>
            <h2 style={H2}>What Happens When You Call DRR</h2>
            <div style={{ ...GOLDLINE, margin: "16px auto" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
            {[
              { step: "01", title: "You Call", desc: "We begin triaging and mitigating while still on the phone with you." },
              { step: "02", title: "We Arrive", desc: "Uniformed, credentialed DRR employees. You'll know exactly who's in your home." },
              { step: "03", title: "We Explain", desc: "Plain-language walkthrough of the damage, the plan, and the timeline — before we start." },
              { step: "04", title: "We Document", desc: "Written Xactimate scope, photos, moisture readings — everything your insurance needs." },
              { step: "05", title: "We Restore", desc: "Daily updates, one project manager, one number. No surprises. No abandoned jobs." },
            ].map((s) => (
              <div key={s.step} style={{ ...CARD, borderTop: "none", textAlign: "center", padding: "1.5rem" }}>
                <span style={{ color: C.gold, fontFamily: F.display, fontWeight: 800, fontSize: "2rem", display: "block", marginBottom: 8 }}>{s.step}</span>
                <h3 style={{ ...H3, fontSize: "1.1rem", marginBottom: 8 }}>{s.title}</h3>
                <p style={{ ...P, fontSize: "0.82rem" }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ EMERGENCY CTA ═══ */}
      <section style={{ ...SEC, minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Image src="/images/arizona-landmarks/monument-valley-night-arizona.jpg" alt="Arizona night" fill style={{ objectFit: "cover" }} />
        <div style={OVERLAY(0.55)} />
        <div style={{ ...CONTENT, maxWidth: 750, textAlign: "center" }}>
          <div style={{ ...PANEL, padding: "56px 40px" }}>
            <span style={{ ...LABEL, marginBottom: 20 }}>AVAILABLE 24 · 7 · 365</span>
            <h2 style={{ color: C.white, fontFamily: F.display, fontWeight: 800, fontSize: "clamp(1.8rem, 4vw, 3rem)", lineHeight: 1.08, marginBottom: 16 }}>
              You deserve to know exactly<br />who is in your home.
            </h2>
            <p style={{ ...P, fontSize: "1rem", marginBottom: 36, maxWidth: 520, margin: "0 auto 36px" }}>
              From day one to the day the project is completed. Licensed, verified, and here for you — not just your insurance check.
            </p>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
              </div>
            <p style={{ marginTop: 28, fontFamily: F.mono, fontSize: "0.62rem", letterSpacing: "2px", textTransform: "uppercase", color: C.w30 }}>
              AZ ROC #349012 · BBB A+ · {BRAND.rating}★ {BRAND.reviewCount} Reviews · IICRC Certified
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
